import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Collapse, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ChevronDownIcon, ChevronUpIcon } from "@sx-icons";
import { ng } from "@common/ngds";
import { ControlList } from "./SXRcControlsReport";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectProject, useProject } from "../features/projects/projectSlice";
import { report_exception } from "./ui-components/SXAlert/SXAlertMessage";
import { clearAlerts } from "../helpers/AlertMessage";
import {
  closeDesignReview,
  selectDesignReviewRecommendedProject,
  selectDesignReviewVerified,
  selectDesignReviewSuggestions,
} from "../features/designReview/designReviewSlice";
import {
  collectGoverningControls,
  formatFireProtectionValue,
  formatUtilizationPercent,
  getAutoDesignDbs,
  getConcreteCoverMm,
  getCurrentMaterialGuid,
  getCurrentReinforcement,
  getCurrentSectionGuid,
  getMaxUtilizationValue,
  getProjectFireProtection,
  getSuggestedFireProtection,
  resolveMaterialName,
  resolveSectionName,
  summarizeReinforcement,
  type FireProtectionValue,
  type ReinforcementSummary,
} from "../helpers/designSuggestions";
import { getMemberMaterialGroup } from "../helpers/memberDesign";

type Suggestion = ng.endpoint_args.common_api_functions.AutoDesignSuggestion_json;

// The cover rides in this line rather than in a row of its own: it is a property of the
// reinforcement the design settled on, and the pane has no room to spend a row on it. Null leaves
// it out entirely — see suggestionCover for when a suggestion has no cover to show.
//
// Millimetres, not the project's sectional unit: a cover is edited and read in mm throughout (the
// reinforcement accordion's cover field prints the raw value), which is the one place this app
// departs from the SI base unit it stores lengths in, so the review has to depart with it. The fire
// protection rows alongside are fixed to mm for the same reason.
const reinforcementText = (t: TFunction, summary: ReinforcementSummary, coverMm?: number | null): string =>
  [
    summary.bars && `${t("bars_.label", "Bars")}: ${summary.bars}`,
    summary.strands && `${t("strands_.label", "Strands")}: ${summary.strands}`,
    summary.stirrups && `${t("stirrups_.label", "Stirrups")}: ${summary.stirrups}`,
    coverMm != null && `${t("cover_.label", "Cover")}: ${coverMm} mm`,
  ]
    .filter(Boolean)
    .join(" · ") || "—";

export interface SXDesignReviewPanelProps {
  /**
   * Called once a suggestion has been applied to the project — Approve and re-run, or Apply
   * selected — and just before the analysis and code check are re-run over it. The host uses it to
   * bring the code check into view, which is where the results of that run land. Declining does not
   * fire it, and nor does a failed apply: the project is unchanged and nothing will be run.
   */
  onApproved?: () => void;
}

/**
 * Design review panel. Rendered in place of the member design settings (see MemberDesignPanel)
 * while a completed design run is staged in designReviewSlice. It lets the user review the
 * auto-design suggestion(s) and decide what to apply to the project. Nothing is applied or
 * persisted until the user explicitly approves here; declining (or approving) clears the staged
 * result, which brings the design settings back. Approving also re-runs the analysis and then the
 * code check over what was applied, since the design compared its candidates against the section
 * forces of the analysis that was already in the project (see applySelected):
 *  - one suggestion  -> show what the recommended suggestion changes; approve or decline.
 *  - many suggestions -> compare and pick one; the recommended one applies the returned project
 *    verbatim, any other applies its section/material/reinforcement to the current project.
 *  - no suggestions, but the returned project differs from the current one -> review that project
 *    as a single implied suggestion (see impliedSuggestion). A fallback for a service that answers
 *    a design run with the designed project alone; the FEM-Design service used to do this for
 *    steel and timber, and now reports its recommendation as a suggestion like rc-design does.
 *  - no suggestions and an unchanged project -> nothing was found; project left unchanged.
 */
export const SXDesignReviewPanel: React.FC<SXDesignReviewPanelProps> = ({ onApproved }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { analyseThenCodeCheck, applyDesignSuggestion, applyRecommendedDesignResult } = useProject();

  const stagedSuggestions = useAppSelector(selectDesignReviewSuggestions);
  const recommendedProject = useAppSelector(selectDesignReviewRecommendedProject);
  // The server analysed and code checked the recommended project before returning it (#442).
  const verified = useAppSelector(selectDesignReviewVerified);
  const selProject = useAppSelector(selectProject);

  // The current (pre-apply) frontend project — source of the "before" values and the AutoDesign
  // section/material databases cached for this run.
  const currentProject = selProject?.data ?? null;
  const { secDb, mtrlDb } = useMemo(() => getAutoDesignDbs(currentProject), [currentProject]);

  const materialGroup = getMemberMaterialGroup(currentProject);
  // Only a concrete suggestion carries reinforcement. Steel and timber are designed on their section
  // and fire protection, so a reinforcement row there would show nothing but a dash on both sides.
  const showReinforcement = materialGroup === "Concrete";

  // "before" values from the current project (resolved against the project's own input dbs).
  const inputSecDb = currentProject?.data?.input?.secDb ?? [];
  const inputMtrlDb = currentProject?.data?.input?.mtrlDb ?? [];
  const currentSectionGuid = getCurrentSectionGuid(currentProject);
  const currentMaterialGuid = getCurrentMaterialGuid(currentProject);
  const beforeSectionName = resolveSectionName(currentSectionGuid, inputSecDb);
  const beforeMaterialName = resolveMaterialName(currentMaterialGuid, inputMtrlDb);
  const beforeReinf = getCurrentReinforcement(currentProject);
  const beforeSummary = summarizeReinforcement(beforeReinf.rebars, beforeReinf.links, beforeReinf.strands);
  const beforeCover = getConcreteCoverMm(currentProject);
  // The fire protection the member is designed within — a thickness, or a maximum temperature for a
  // steel member checked against one. A design run varies it alongside the section, so what it
  // settled on is part of what the user is approving.
  const beforeFire = useMemo(
    () => getProjectFireProtection(currentProject, materialGroup),
    [currentProject, materialGroup],
  );

  // A design service may answer with the designed project and no AutoDesignSuggestion entries at
  // all (the FEM-Design service did exactly that for steel and timber before it reported its
  // recommendation as a suggestion). Taken literally that reads as "no suitable design found", and
  // Close would then discard a perfectly good result — leaving those materials with no way to apply
  // a design. So when nothing was suggested but the returned project differs from the current one,
  // the returned project *is* the suggestion: its section, material, reinforcement and fire
  // protection are read off the project itself and its checks out of its output, which is all the
  // review needs. If it came back identical, nothing was found after all and the empty body still
  // tells that truth.
  const impliedSuggestion = useMemo<Suggestion | null>(() => {
    if (stagedSuggestions.length > 0 || !recommendedProject) return null;

    const sectionGuid = getCurrentSectionGuid(recommendedProject) ?? "";
    const materialGuid = getCurrentMaterialGuid(recommendedProject) ?? "";
    const reinf = getCurrentReinforcement(recommendedProject);
    const afterSummary = summarizeReinforcement(reinf.rebars, reinf.links, reinf.strands);
    // The protection is designed alongside the section, so a run that kept the section and only
    // moved the protection thickness (or the temperature it was checked against) is still a result
    // worth reviewing — without this it would read as "nothing found" and be thrown away.
    const fireText = (values: FireProtectionValue[]) =>
      values.map((value) => `${value.labelKey}:${formatFireProtectionValue(value)}`).join("|");

    const changed =
      sectionGuid !== (currentSectionGuid ?? "") ||
      materialGuid !== (currentMaterialGuid ?? "") ||
      reinforcementText(t, afterSummary) !== reinforcementText(t, beforeSummary) ||
      fireText(getProjectFireProtection(recommendedProject, materialGroup)) !== fireText(beforeFire);
    if (!changed) return null;

    return {
      isRecommended: true,
      sectionGuid,
      materialGuid,
      controls: collectGoverningControls(recommendedProject),
      concrete: { rebars: reinf.rebars, strands: reinf.strands, links: reinf.links },
    };
    // beforeSummary is derived from currentProject, so the guids below cover it as dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stagedSuggestions,
    recommendedProject,
    currentSectionGuid,
    currentMaterialGuid,
    currentProject,
    materialGroup,
    beforeFire,
  ]);

  const suggestions = useMemo<Suggestion[]>(
    () => (stagedSuggestions.length > 0 ? stagedSuggestions : impliedSuggestion ? [impliedSuggestion] : []),
    [stagedSuggestions, impliedSuggestion],
  );

  const recommendedIndex = useMemo(() => suggestions.findIndex((s) => s.isRecommended), [suggestions]);
  const isMulti = suggestions.length > 1;

  // Is this suggestion the one the design service applied to the project it answered with? That
  // project is the designed member, fully calculated, so wherever a suggestion is that one it is
  // both what gets committed on approval and where its own designed values can be read off. A lone
  // suggestion counts whether or not it is flagged as recommended: with only one result there is
  // nothing else the returned project could be showing.
  const isReturnedProject = (suggestion: Suggestion): boolean =>
    recommendedProject != null && (suggestion.isRecommended || suggestions.length === 1);

  // The governing checks in the project the design engine returned — the worst utilization per
  // distinct check, exactly what the controls report reads off that same output. Computed once:
  // every suggestion the returned project was designed to falls back to it (see
  // suggestionControls), and the multi-suggestion list asks per card.
  const projectControls = useMemo(() => collectGoverningControls(recommendedProject), [recommendedProject]);

  // The checks a suggestion was found to satisfy. The rc-design service reports them on the
  // suggestion itself; the steel and timber services deliberately answer with an empty list and
  // put the checks in the project they designed instead (that project is the code check of the
  // recommendation), so a suggestion the returned project was designed to falls back to it. Any
  // other suggestion has no project of its own to fall back on: its section was never checked,
  // which is why applying one invalidates the output and asks for a fresh run.
  const suggestionControls = (suggestion: Suggestion): ng.result.control.ControlData_json[] => {
    const reported = suggestion.controls ?? [];
    if (reported.length > 0) return reported;
    return isReturnedProject(suggestion) ? projectControls : [];
  };

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  // Which suggestion's check list is expanded, keyed by suggestion index. The lists are long, so
  // they stay collapsed until the user asks for them.
  const [checksOpen, setChecksOpen] = useState<Record<number, boolean>>({});
  const [expandedControls, setExpandedControls] = useState<Record<number, boolean>>({});

  // When a new result arrives, default the selection to the recommended suggestion and reset the
  // drilldown state.
  useEffect(() => {
    setSelectedIndex(recommendedIndex);
    setDetailIndex(suggestions.length === 1 ? 0 : null);
    setChecksOpen({});
    setExpandedControls({});
  }, [recommendedIndex, suggestions]);

  const close = () => dispatch(closeDesignReview());

  // Apply the recommended suggestion by committing the project the design engine returned — its
  // input, that is: the output it arrives with is dropped, since the re-run below produces the
  // controls the user reads. If the engine returned no project, surface an error rather than
  // silently closing so the user isn't misled into thinking it applied. Answers whether anything
  // was applied, so the caller knows whether there is a run to trigger.
  const applyRecommended = (): boolean => {
    if (!recommendedProject) {
      report_exception(t, new Error(t("design_apply_no_project_.label", "No design result to apply.")));
      return false;
    }
    applyRecommendedDesignResult(recommendedProject, verified);
    close();
    return true;
  };

  // Apply a non-recommended suggestion: swap the element's section + material and replace its
  // reinforcement (via the applyDesignSuggestion thunk, which also recomputes partial factors).
  // Whatever output the project had is invalidated on the way, which the re-run then replaces. If
  // the suggestion can't be resolved the thunk throws and we surface it, leaving the project
  // unchanged — and, because false comes back, unrun.
  const applyChosen = async (suggestion: Suggestion): Promise<boolean> => {
    try {
      await applyDesignSuggestion(suggestion, secDb, mtrlDb);
      close();
      return true;
    } catch (error) {
      report_exception(t, error);
      return false;
    }
  };

  // Approve/apply the selected suggestion, then re-run the analysis and the code check over it. The
  // recommended one commits the returned project verbatim; any other applies its inputs to the
  // current project.
  //
  // "The recommended one" is isReturnedProject: rebuilding a suggestion the service already
  // designed from its own guids would recompute the partial factors and throw the design's checks
  // away for nothing.
  //
  // The re-run is what makes the applied result trustworthy: the design engine iterates over
  // candidate sections without re-analysing, so every candidate was checked against the section
  // forces of the analysis that was already in the project. A different section changes the
  // column's self-weight and stiffness, so the analysis — and then the check — has to run over the
  // section that was actually applied. That holds for every material, and for the recommended
  // suggestion too: it arrives carrying results, which is exactly what makes the staleness easy to
  // miss. Doing it here is why the pane no longer has to ask the user to do it by hand.
  const applySelected = async (suggestion: Suggestion | undefined) => {
    if (!suggestion) return;
    const returned = isReturnedProject(suggestion);
    const applied = returned ? applyRecommended() : await applyChosen(suggestion);
    if (!applied) return;
    // Applying closed the review, so hand off to the code check before the run rather than after
    // it: the user watches the controls land there (under the calculation overlay, over the report's
    // empty state — applying invalidated the output) instead of waiting on a pane that has gone.
    onApproved?.();
    // Drop the previous run's alerts (and their duplicate-suppression markers) so run-specific
    // messages such as the near-critical / critical-load warnings can be raised again — the same
    // reset the toolbar's Analysis button does before a manual run.
    clearAlerts();
    // A verified recommendation arrives with its own analysis and code check: the server ran exactly
    // this re-run before returning it, so running it again would only make the user wait.
    if (returned && verified) return;
    await analyseThenCodeCheck();
  };

  // "after" identities resolve against the AutoDesign databases the run was built with, which hold
  // every candidate. The returned project's own databases come next: for an implied suggestion the
  // section is whatever the engine put in the project, and nothing guarantees it came from the
  // candidate list — a name is what the user reads here, so do not let it fall back to a dash.
  const afterSecDb = useMemo(
    () => [...secDb, ...(recommendedProject?.data?.input?.secDb ?? [])],
    [secDb, recommendedProject],
  );
  const afterMtrlDb = useMemo(
    () => [...mtrlDb, ...(recommendedProject?.data?.input?.mtrlDb ?? [])],
    [mtrlDb, recommendedProject],
  );

  // The fire protection a suggestion ends up with. The design service reports it on the suggestion
  // itself; a service that predates that leaves it empty, so a suggestion the returned project was
  // designed to falls back to that project — the design writes the values it settled on onto its
  // element. Any other suggestion has no project of its own to fall back on.
  const suggestionFireProtection = (suggestion: Suggestion): FireProtectionValue[] => {
    const reported = getSuggestedFireProtection(suggestion, materialGroup);
    if (reported.length > 0) return reported;
    return isReturnedProject(suggestion) ? getProjectFireProtection(recommendedProject, materialGroup) : [];
  };

  // The concrete cover a suggestion ends up with. The design engine resolves the cover itself, per
  // candidate bar diameter, rather than using the one the project went in with, and writes what it
  // used onto the element of the project it returns — which is also what gets applied to the
  // project input on approval. So it is a designed quantity the user is approving sight unseen
  // unless the review shows it, and the returned project is the only place it can be read: a
  // suggestion that project was not designed to was never cover-resolved and has none of its own.
  const suggestionCover = (suggestion: Suggestion): number | null =>
    isReturnedProject(suggestion) ? getConcreteCoverMm(recommendedProject) : null;

  const renderDiffTable = (suggestion: Suggestion) => {
    const afterSectionName = resolveSectionName(suggestion.sectionGuid, afterSecDb);
    const afterMaterialName = resolveMaterialName(suggestion.materialGuid, afterMtrlDb);
    const afterSummary = summarizeReinforcement(
      suggestion.concrete?.rebars,
      suggestion.concrete?.links,
      suggestion.concrete?.strands,
    );

    // "changed" is decided on the underlying identity (section/material GUID) or the reinforcement
    // summary — not the displayed name, so an unnamed section can't hide a real change.
    const rows: { label: string; before: string; after: string; changed: boolean }[] = [
      {
        label: t("section_.label", "Section"),
        before: beforeSectionName,
        after: afterSectionName,
        changed: !!suggestion.sectionGuid && suggestion.sectionGuid !== currentSectionGuid,
      },
      {
        label: t("material_.label", "Material"),
        before: beforeMaterialName,
        after: afterMaterialName,
        changed: !!suggestion.materialGuid && suggestion.materialGuid !== currentMaterialGuid,
      },
    ];

    if (showReinforcement) {
      // A suggestion with no cover of its own drops it from *both* columns rather than pairing the
      // project's current cover against a blank, which would read as "the design removed the
      // cover". The cover being part of the text means a cover-only change still flags the row as
      // changed, which is the point of showing it.
      const afterCover = suggestionCover(suggestion);
      const before = reinforcementText(t, beforeSummary, afterCover != null ? beforeCover : null);
      const after = reinforcementText(t, afterSummary, afterCover);
      rows.push({
        label: t("reinforcement_.label", "Reinforcement"),
        before,
        after,
        changed: before !== after,
      });
    }

    // One row per quantity either side carries, keyed by its label: a protection that gained or
    // lost a layer — or a steel member switched between a material and a maximum temperature —
    // still shows both sides of the change rather than silently dropping one.
    const afterFire = suggestionFireProtection(suggestion);
    const fireKeys = [...new Set([...beforeFire, ...afterFire].map((value) => value.labelKey))];
    for (const labelKey of fireKeys) {
      const before = formatFireProtectionValue(beforeFire.find((value) => value.labelKey === labelKey));
      const after = formatFireProtectionValue(afterFire.find((value) => value.labelKey === labelKey));
      rows.push({ label: t(labelKey), before, after, changed: before !== after });
    }

    return (
      <div className="sx-dr-table-scroll">
        <Table size="sm" bordered className="mb-2 sx-dr-diff-table">
          <thead>
            <tr>
              <th />
              <th>{t("current_.label", "Current")}</th>
              <th>{t("suggested_.label", "Suggested")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const changed = row.changed;
              return (
                <tr key={row.label}>
                  <td className="fw-semibold">{row.label}</td>
                  <td className="text-muted">{row.before}</td>
                  <td className={changed ? "fw-semibold" : undefined}>
                    {row.after}
                    {changed && (
                      <Badge bg="warning" text="dark" className="ms-2">
                        {t("changed_.label", "changed")}
                      </Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // The check list dominates the pane, so it sits behind its own per-suggestion toggle and starts
  // collapsed. Only one suggestion's checks are rendered at a time (a suggestion's checks live
  // inside its details section), so the shared `expandedControls` indices can't collide.
  const renderControls = (suggestion: Suggestion, index: number) => {
    const controls = suggestionControls(suggestion);
    if (controls.length === 0) {
      return <p className="text-muted small mb-0">{t("no_controls_.label", "No checks available.")}</p>;
    }
    const isOpen = !!checksOpen[index];
    return (
      <div className="sx-dr-checks">
        <Button
          variant="link"
          className="sx-dr-checks-toggle"
          aria-expanded={isOpen}
          onClick={() => {
            setChecksOpen((prev) => ({ ...prev, [index]: !prev[index] }));
            // Collapse the individual control accordions so the list opens compact every time.
            setExpandedControls({});
          }}
        >
          <span className="sx-dr-checks-caret">
            {isOpen ? <ChevronUpIcon size={12} /> : <ChevronDownIcon size={12} />}
          </span>
          <span className="fw-semibold">{t("governing_checks_.label", "Checks")}</span>
          <Badge bg="secondary" className="ms-1">
            {controls.length}
          </Badge>
        </Button>
        <Collapse in={isOpen}>
          <div>
            <div className="sx-dr-checks-body">
              <ControlList controls={controls} expanded={expandedControls} setExpanded={setExpandedControls} t={t} />
            </div>
          </div>
        </Collapse>
      </div>
    );
  };

  // ---- Body variants ------------------------------------------------------

  const renderEmptyBody = () => (
    <Alert variant="warning" className="mb-0">
      <Alert.Heading className="h6">
        {t("no_design_suggestions_title_.label", "No suitable design found")}
      </Alert.Heading>
      <p className="mb-0">
        {t(
          "no_design_suggestions_help_.label",
          "The design constraints were too tight to satisfy. Loosen the size limits, bar/stirrup choices, or the target utilization and run the design again. Your project has not been changed.",
        )}
      </p>
    </Alert>
  );

  const renderSingleBody = () => {
    const suggestion = suggestions[0];
    const maxUtil = getMaxUtilizationValue(suggestionControls(suggestion));
    return (
      <div>
        <p className="sx-dr-help">
          {t(
            "single_suggestion_help_.label",
            "The design produced a single result. Review the changes below and approve to apply them, or decline to keep your project unchanged.",
          )}
        </p>
        <div className="sx-dr-util">
          <span className="sx-dr-util-label">{t("max_utilization_.label", "Max utilization")}</span>
          <span
            className={`sx-dr-util-value ${maxUtil == null ? "text-muted" : maxUtil > 1 ? "text-danger" : "text-success"}`}
          >
            {formatUtilizationPercent(maxUtil)}
          </span>
        </div>
        {renderDiffTable(suggestion)}
        {renderControls(suggestion, 0)}
      </div>
    );
  };

  // In the side pane there is no room for the wide comparison table the modal used, so each
  // suggestion is a selectable card with its key figures stacked and the diff/checks behind a
  // details toggle.
  const renderMultiBody = () => (
    <div>
      <p className="sx-dr-help">
        {t(
          "multi_suggestion_help_.label",
          "The design produced several results. Select one to apply. The recommended result is preselected; expand a row to see the exact changes and checks.",
        )}
      </p>
      {suggestions.map((suggestion, index) => {
        const maxUtil = getMaxUtilizationValue(suggestionControls(suggestion));
        const summary = summarizeReinforcement(
          suggestion.concrete?.rebars,
          suggestion.concrete?.links,
          suggestion.concrete?.strands,
        );
        const isDetail = detailIndex === index;
        const isSelected = selectedIndex === index;
        return (
          <div key={index} className={`sx-dr-card ${isSelected ? "is-selected" : ""}`}>
            <div className="sx-dr-card-head">
              <Form.Check
                type="radio"
                id={`design-suggestion-${index}`}
                name="design-suggestion"
                checked={isSelected}
                onChange={() => setSelectedIndex(index)}
                aria-label={t("select_suggestion_.label", "Select suggestion")}
              />
              <span className="sx-dr-card-title">#{index + 1}</span>
              {suggestion.isRecommended && (
                <Badge bg="success" className="ms-1">
                  {t("recommended_.label", "Recommended")}
                </Badge>
              )}
              <span className={`sx-dr-card-util ms-auto ${maxUtil != null && maxUtil > 1 ? "text-danger" : ""}`}>
                {formatUtilizationPercent(maxUtil)}
              </span>
            </div>
            <dl className="sx-dr-card-facts">
              <dt>{t("section_.label", "Section")}</dt>
              <dd>{resolveSectionName(suggestion.sectionGuid, afterSecDb)}</dd>
              <dt>{t("material_.label", "Material")}</dt>
              <dd>{resolveMaterialName(suggestion.materialGuid, afterMtrlDb)}</dd>
              {showReinforcement && (
                <>
                  <dt>{t("reinforcement_.label", "Reinforcement")}</dt>
                  <dd>{reinforcementText(t, summary, suggestionCover(suggestion))}</dd>
                </>
              )}
              {/* A fire protected member is designed on its protection as much as on its section,
                  so the thickness — or the maximum temperature — it ended up at is a key figure
                  the cards have to carry, not a detail behind the toggle. */}
              {suggestionFireProtection(suggestion).map((value) => (
                <React.Fragment key={value.labelKey}>
                  <dt>{t(value.labelKey)}</dt>
                  <dd>{formatFireProtectionValue(value)}</dd>
                </React.Fragment>
              ))}
            </dl>
            <Button
              size="sm"
              variant="link"
              className="sx-dr-details-toggle"
              onClick={() => {
                setDetailIndex(isDetail ? null : index);
                // Details always open compact — checks collapsed, control accordions closed.
                setChecksOpen({});
                setExpandedControls({});
              }}
            >
              {isDetail ? t("hide_.label", "Hide") : t("details_.label", "Details")}
            </Button>
            <Collapse in={isDetail}>
              <div>
                <div className="sx-dr-card-details">
                  {renderDiffTable(suggestion)}
                  {renderControls(suggestion, index)}
                </div>
              </div>
            </Collapse>
          </div>
        );
      })}
    </div>
  );

  // ---- Assemble body + actions -------------------------------------------

  const hasSuggestions = suggestions.length > 0;

  let body: React.ReactElement;
  if (!hasSuggestions) {
    body = renderEmptyBody();
  } else if (isMulti) {
    body = renderMultiBody();
  } else {
    body = renderSingleBody();
  }

  let actions: React.ReactElement;
  if (!hasSuggestions) {
    actions = (
      <Button className="secondary-btn w-100 ms-0 mx-0" onClick={close}>
        {t("close_.label", "Close")}
      </Button>
    );
  } else if (isMulti) {
    // Stacked in the pane, so the primary action comes first (where the Design button sat).
    actions = (
      <>
        <Button
          id="design_review_apply_selected"
          className="primary-btn w-100 ms-0 mx-0"
          onClick={() => void applySelected(suggestions[selectedIndex])}
          disabled={selectedIndex < 0}
        >
          {t("apply_selected_.label", "Apply selected")}
        </Button>
        <Button className="secondary-btn w-100 ms-0 mx-0" onClick={close}>
          {t("cancel_.label", "Cancel")}
        </Button>
      </>
    );
  } else {
    actions = (
      <>
        <Button
          id="design_review_approve"
          className="primary-btn w-100 ms-0 mx-0"
          onClick={() => void applySelected(suggestions[0])}
        >
          {verified && isReturnedProject(suggestions[0])
            ? t("approve_verified_.label", "Approve")
            : t("approve_and_rerun_.label", "Approve and re-run")}
        </Button>
        <Button id="design_review_decline" className="secondary-btn w-100 ms-0 mx-0" onClick={close}>
          {t("decline_.label", "Decline")}
        </Button>
      </>
    );
  }

  const header = hasSuggestions
    ? t("design_suggestions_title_.label", "Review design result")
    : t("design_no_result_title_.label", "Design result");

  // There is no standing "the design does not re-run the analysis" notice any more: applying a
  // result now runs the analysis and then the code check itself (see applySelected), so nothing is
  // left for the user to remember to do by hand.
  return (
    <div className="sx-design-review">
      <div className="header sx-cd-section-title sx-dr-header">{header}</div>
      <div className="sx-dr-body">{body}</div>
      <div className="sx-dr-actions">{actions}</div>
    </div>
  );
};

export default SXDesignReviewPanel;
